import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from './useSuiteStore'

interface PaginatedTodos {
  data: any[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export const useTodoStore = defineStore('todo', () => {
  const suiteStore = useSuiteStore()

  const todos = ref<PaginatedTodos | null>({
    data: [],
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0,
  })

  const currentTodo = ref<any | null>(null)
  const isLoadingTodos = ref(false)
  const isLoadingCurrentTodo = ref(false)

  /**
   * Récupère la liste des todos avec pagination et filtres
   */
  const fetchTodos = async (filters: any = {}, pageSize = 50, page = 1) => {
    isLoadingTodos.value = true
    const body = {
      filters,
      limit: pageSize,
      page,
    }
    try {
      const response = await suiteStore.suiteFetch('todos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        todos.value = {
          data: Array.isArray(data.data) ? data.data : (data.data ?? []),
          total: data?.total ?? 0,
          page: data?.page ?? page,
          pageSize: data?.pageSize ?? pageSize,
          totalPages: data?.totalPages ?? 1,
        }
        return todos.value
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des todos:', error)
    } finally {
      isLoadingTodos.value = false
    }
    return null
  }

  /**
   * Récupère un todo par son UUID
   */
  const fetchTodoByUuid = async (uuid: string) => {
    isLoadingCurrentTodo.value = true
    try {
      const response = await suiteStore.suiteFetch(`todos/${uuid}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })
      if (response && typeof response !== 'boolean' && response.ok) {
        const data = await response.json()
        currentTodo.value = data
        return data
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du todo:', error)
    } finally {
      isLoadingCurrentTodo.value = false
    }
    return null
  }

  /**
   * Crée un nouveau todo
   */
  const createTodo = async (todoData: any): Promise<{ ok: boolean; todoUuid?: string; errorMessage?: string }> => {
    try {
      const response = await suiteStore.suiteFetch('todos/todo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(todoData),
        credentials: 'include',
      })

      if (response && typeof response !== 'boolean') {
        if (response.ok) {
          const data = await response.json()
          return { ok: true, todoUuid: data.uuid }
        } else {
          const errorData = await response.json().catch(() => ({}))
          return {
            ok: false,
            errorMessage: errorData.message || `Erreur HTTP ${response.status}`,
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors de la création du todo:', error)
      return { ok: false, errorMessage: 'Erreur réseau ou serveur' }
    }
    return { ok: false, errorMessage: 'Suite non disponible' }
  }

  /**
   * Met à jour un todo existant
   */
  const updateTodo = async (uuid: string, todoData: any): Promise<{ ok: boolean; errorMessage?: string }> => {
    try {
      const response = await suiteStore.suiteFetch(`todos/${uuid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(todoData),
        credentials: 'include',
      })

      if (response && typeof response !== 'boolean') {
        if (response.ok) {
          const data = await response.json()
          currentTodo.value = data
          return { ok: true }
        } else {
          const errorData = await response.json().catch(() => ({}))
          return {
            ok: false,
            errorMessage: errorData.message || `Erreur HTTP ${response.status}`,
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du todo:', error)
      return { ok: false, errorMessage: 'Erreur réseau ou serveur' }
    }
    return { ok: false, errorMessage: 'Suite non disponible' }
  }

  /**
   * Supprime un todo
   */
  const deleteTodo = async (uuid: string): Promise<{ ok: boolean; errorMessage?: string }> => {
    try {
      const response = await suiteStore.suiteFetch(`todos/${uuid}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      })

      if (response && typeof response !== 'boolean') {
        if (response.ok) {
          return { ok: true }
        } else {
          const errorData = await response.json().catch(() => ({}))
          return {
            ok: false,
            errorMessage: errorData.message || `Erreur HTTP ${response.status}`,
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du todo:', error)
      return { ok: false, errorMessage: 'Erreur réseau ou serveur' }
    }
    return { ok: false, errorMessage: 'Suite non disponible' }
  }

  return {
    todos,
    currentTodo,
    isLoadingTodos,
    isLoadingCurrentTodo,
    fetchTodos,
    fetchTodoByUuid,
    createTodo,
    updateTodo,
    deleteTodo,
  }
})
